/** Suggestions inbox — one click per fix, reason required, bulk where it makes sense (spec §4.2). */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, RefreshCw, CheckSquare } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import ReasonDialog from './ReasonDialog';
import { ACTION, SUGGESTION, api, employeeName, errorText, hm, label } from './shared';

const BLOCKING = ['missing_punch', 'single_shift', 'long_span', 'absence', 'leave_pending', 'punch_during_leave'];
const NO_CLICK = ['ask_employee', 'ask_manager', 'approve_leave', 'shorten_leave', 'investigate', 'inform_branch_manager'];

function evidenceText(ev, isRTL) {
  return Object.entries(ev || {})
    .filter(([, v]) => v !== null && v !== '' && v !== undefined)
    .filter(([k]) => !['request_id'].includes(k))
    .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${k.endsWith('minutes') ? hm(v) : v}`)
    .join(' · ');
}

export default function InboxTab({ token, isRTL, go }) {
  const t = (en, ar) => (isRTL ? ar : en);
  const [params] = useSearchParams();
  const [status, setStatus] = useState('open');
  const [type, setType] = useState('');
  const [dayFrom, setDayFrom] = useState('');
  const [dayTo, setDayTo] = useState('');
  const branch = params.get('branch') || '';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(null);       // {items: [...], action}
  const [selected, setSelected] = useState({});

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const p = { status, limit: 500 };
      if (type) p.type = type;
      if (dayFrom) p.day_from = dayFrom;
      if (dayTo) p.day_to = dayTo;
      if (branch) p.branch_id = branch;
      setItems((await api(token).get('/suggestions', p)).items);
      setSelected({});
    } catch (e) { setError(errorText(e)); } finally { setLoading(false); }
  }, [token, status, type, dayFrom, dayTo, branch]);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => items.reduce((a, s) => ({ ...a, [s.type]: (a[s.type] || 0) + 1 }), {}), [items]);
  const chosen = items.filter((s) => selected[s.id]);
  const commonActions = useMemo(() => {
    if (!chosen.length) return [];
    const keys = chosen.map((s) => s.actions.filter((a) => a.adjustments.length).map((a) => a.key));
    return keys.reduce((acc, k) => acc.filter((x) => k.includes(x)));
  }, [chosen]);

  const decide = async (reason) => {
    const { items: targets, action } = pending;
    let ok = 0;
    for (const s of targets) {
      try {
        await api(token).post(`/suggestions/${s.id}/decide`, { action_key: action, reason });
        ok += 1;
      } catch (e) { toast.error(`${employeeName(s.employee, isRTL)} ${s.day}: ${errorText(e)}`); }
    }
    if (ok) toast.success(t(`${ok} item(s) done`, `تم تنفيذ ${ok}`));
    setPending(null);
    load();
  };

  return (
    <div className="space-y-4 mt-4" data-testid="attendance-inbox">
      <div className="flex flex-wrap items-center gap-2">
        <select className="border rounded-md px-3 py-2 text-sm bg-white" value={status} onChange={(e) => setStatus(e.target.value)}
          data-testid="inbox-status">
          <option value="open">{t('Open', 'مفتوحة')}</option>
          <option value="applied">{t('Applied', 'منفذة')}</option>
          <option value="dismissed">{t('Dismissed', 'مستبعدة')}</option>
          <option value="resolved">{t('Resolved by itself', 'حُلّت تلقائياً')}</option>
        </select>
        <select className="border rounded-md px-3 py-2 text-sm bg-white" value={type} onChange={(e) => setType(e.target.value)}
          data-testid="inbox-type">
          <option value="">{t('All types', 'كل الأنواع')}</option>
          {Object.keys(SUGGESTION).map((k) => <option key={k} value={k}>{label(SUGGESTION, k, isRTL)}</option>)}
        </select>
        <Input type="date" value={dayFrom} onChange={(e) => setDayFrom(e.target.value)} className="w-40" />
        <Input type="date" value={dayTo} onChange={(e) => setDayTo(e.target.value)} className="w-40" />
        {branch && <Badge variant="outline">{t('Branch filter on', 'تصفية حسب الفرع')}</Badge>}
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
        <span className="text-sm text-slate-500 ms-auto">{items.length} {t('items', 'عنصر')}</span>
      </div>

      {!!Object.keys(counts).length && (
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([k, n]) => (
            <button key={k} onClick={() => setType(type === k ? '' : k)}
              className={`text-xs border rounded-full px-2 py-0.5 ${type === k ? 'bg-brand-black text-white' : 'bg-white'}`}>
              {label(SUGGESTION, k, isRTL)}: {n}
            </button>
          ))}
        </div>
      )}

      {status === 'open' && chosen.length > 0 && (
        <Card className="border-brand-orange">
          <CardContent className="py-3 flex flex-wrap items-center gap-2">
            <CheckSquare className="h-4 w-4 text-brand-orange" />
            <span className="text-sm">{t(`${chosen.length} selected — apply to all:`, `${chosen.length} محددة — تطبيق على الكل:`)}</span>
            {commonActions.map((a) => (
              <Button key={a} size="sm" onClick={() => setPending({ items: chosen, action: a })} data-testid={`inbox-bulk-${a}`}>
                {label(ACTION, a, isRTL)}
              </Button>
            ))}
            {!commonActions.length && <span className="text-sm text-slate-500">{t('No common action', 'لا يوجد إجراء مشترك')}</span>}
          </CardContent>
        </Card>
      )}

      {error && <div className="text-sm text-rose-600">{error}</div>}
      {!loading && !items.length && (
        <div className="brand-empty-state p-8 text-center text-slate-500" data-testid="inbox-empty">
          {status === 'open' ? t('Nothing waiting. Well done.', 'لا يوجد شيء بانتظارك.') : t('Nothing to show.', 'لا توجد بيانات.')}
        </div>
      )}

      <div className="space-y-2">
        {items.map((s) => (
          <Card key={s.id} data-testid={`inbox-item-${s.type}`}>
            <CardContent className="py-3 flex flex-col md:flex-row md:items-center gap-3">
              {status === 'open' && (
                <input type="checkbox" className="h-4 w-4" checked={!!selected[s.id]}
                  onChange={(e) => setSelected({ ...selected, [s.id]: e.target.checked })} aria-label="select" />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <button className="font-semibold text-slate-900 hover:underline"
                    onClick={() => go('employee', { employee: s.employee_id, month: s.day.slice(0, 7) })}>
                    {employeeName(s.employee, isRTL) || s.employee_id}
                  </button>
                  <span className="text-sm text-slate-500">{s.day}</span>
                  <Badge variant={BLOCKING.includes(s.type) ? 'destructive' : 'secondary'}>{label(SUGGESTION, s.type, isRTL)}</Badge>
                  {s.status !== 'open' && <Badge variant="outline">{s.action_key ? label(ACTION, s.action_key, isRTL) : s.status}</Badge>}
                </div>
                <div className="text-xs text-slate-500 mt-1">{evidenceText(s.evidence, isRTL)}</div>
                {s.decision_reason && <div className="text-xs text-slate-600 mt-1">{t('Reason', 'السبب')}: {s.decision_reason}</div>}
              </div>
              {s.status === 'open' && (
                <div className="flex flex-wrap gap-1.5">
                  {s.actions.map((a) => {
                    const params = Object.values(a.params || {}).join('–');
                    const info = NO_CLICK.includes(a.key) && !a.adjustments.length;
                    return (
                      <Button key={a.key} size="sm" variant={info ? 'ghost' : 'outline'}
                        onClick={() => setPending({ items: [s], action: a.key })} data-testid={`inbox-action-${a.key}`}>
                        {label(ACTION, a.key, isRTL)}{params ? ` (${params})` : ''}
                      </Button>
                    );
                  })}
                  {!BLOCKING.includes(s.type) && (
                    <Button size="sm" variant="ghost" className="text-slate-500"
                      onClick={() => setPending({ items: [s], action: null })} data-testid="inbox-dismiss">
                      {t('Dismiss', 'استبعاد')}
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <ReasonDialog
        open={!!pending}
        isRTL={isRTL}
        title={pending ? (pending.action ? label(ACTION, pending.action, isRTL) : t('Dismiss', 'استبعاد')) : ''}
        description={pending && (pending.items.length > 1
          ? t(`Applies to ${pending.items.length} items. The original punches are never changed; this can be undone.`,
            `يُطبّق على ${pending.items.length} عناصر. لا تتغير البصمات الأصلية، ويمكن التراجع.`)
          : t('The original punches are never changed; this can be undone from the employee month.',
            'لا تتغير البصمات الأصلية، ويمكن التراجع من شهر الموظف.'))}
        onCancel={() => setPending(null)}
        onConfirm={decide}
      />
    </div>
  );
}
